import { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  saveCommercialDetails,
  updateCommercial,
} from "../Api/CommercialMaster";
import { fetchCurrencies } from "../Api/ViewCurrencyMaster"; // Import the currency API function

const CommercialModal = ({
  show,
  onHide,
  commercial,
  projectId,
  onCommercialSaved,
}) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;
  const orgId = userDetails?.organisationId;

  const [formData, setFormData] = useState({
    commercialTitle: "",
    commercialDescription: "",
    commercialProjectBelong: projectId || "",
    commercialPlannedRevenue: "",
    commercialCurrency: "",
    commercialType: "", // New field
    orgId: String(orgId) || "",
    createdBy: String(userId) || "",
  });

  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]); // State for currency options
  const [currencyLoading, setCurrencyLoading] = useState(true); // Loading state for currencies
  const [errors, setErrors] = useState({});

  // Commercial Type options
  const commercialTypeOptions = [
    { value: "Planned", label: "Planned" },
    { value: "Benefit to Business", label: "Benefit to Business" },
  ];

  // Fetch currencies from API on component mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrencyLoading(true);
        const currencyData = await fetchCurrencies();

        // Transform the API response to match the expected format
        const transformedCurrencies = currencyData.map((currency) => ({
          value: currency.currencyCode,
          label: `${currency.currencyCode} - ${currency.currencyName} (${currency.symbol})`,
          originalData: currency, // Keep original data if needed
        }));

        setCurrencies(transformedCurrencies);
      } catch (error) {
        console.error("Error fetching currencies:", error);
        toast.error("Failed to load currency data");
        // Fallback to empty array
        setCurrencies([]);
      } finally {
        setCurrencyLoading(false);
      }
    };

    if (show) {
      loadCurrencies();
    }
  }, [show]); // Fetch when modal is shown

  useEffect(() => {
    if (commercial) {
      // Edit mode
      setFormData({
        commercialTitle: commercial.commercialTitle || "",
        commercialDescription: commercial.commercialDescription || "",
        commercialProjectBelong:
          commercial.commercialProjectBelong || projectId || "",
        commercialPlannedRevenue: commercial.commercialPlannedRevenue || "",
        commercialCurrency: commercial.commercialCurrency || "",
        commercialType: commercial.commercialType || "", // New field
        orgId: commercial.orgId || String(orgId) || "",
        createdBy: commercial.createdBy || userId?.toString() || "",
      });
    } else {
      // Create mode
      setFormData({
        commercialTitle: "",
        commercialDescription: "",
        commercialProjectBelong: projectId || "",
        commercialPlannedRevenue: "",
        commercialCurrency: "",
        commercialType: "", // New field
        orgId: String(orgId) || "",
        createdBy: String(userId?.toString()) || "",
      });
    }
    setErrors({});
  }, [commercial, show, projectId, userId, orgId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.commercialTitle.trim()) {
      newErrors.commercialTitle = "Commercial title is required";
    }

    if (!formData.commercialProjectBelong) {
      newErrors.commercialProjectBelong = "Project ID is required";
    }

    if (!formData.orgId) {
      newErrors.orgId = "Organization ID is required";
    }

    if (
      formData.commercialPlannedRevenue &&
      isNaN(formData.commercialPlannedRevenue)
    ) {
      newErrors.commercialPlannedRevenue =
        "Planned revenue must be a valid number";
    }

    // Validate commercial type if required
    if (!formData.commercialType) {
      newErrors.commercialType = "Commercial type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (commercial) {
        // Update existing commercial
        const updateData = {
          commercialTitle: formData.commercialTitle,
          commercialDescription: formData.commercialDescription,
          commercialProjectBelong: formData.commercialProjectBelong,
          commercialPlannedRevenue: formData.commercialPlannedRevenue,
          commercialCurrency: formData.commercialCurrency,
          commercialType: formData.commercialType, // New field
          orgId: formData.orgId,
          changedBy: userId?.toString() || "",
        };
        await updateCommercial(commercial.commercialId, updateData);
        toast.success("Commercial updated successfully");
      } else {
        // Create new commercial
        await saveCommercialDetails(formData);
        toast.success("Commercial created successfully");
      }

      onCommercialSaved();
      onHide();
    } catch (error) {
      console.error("Error saving commercial:", error);
      toast.error(error.message || "Failed to save commercial");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const formatCurrency = (value) => {
    // Remove any non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, "");
    return numericValue;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {commercial ? "Edit Commercial" : "Add New Commercial"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {errors.general && <Alert variant="danger">{errors.general}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label>Commercial Title *</Form.Label>
            <Form.Control
              type="text"
              value={formData.commercialTitle}
              onChange={(e) => handleChange("commercialTitle", e.target.value)}
              isInvalid={!!errors.commercialTitle}
              placeholder="Enter commercial title"
            />
            <Form.Control.Feedback type="invalid">
              {errors.commercialTitle}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Commercial Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.commercialDescription}
              onChange={(e) =>
                handleChange("commercialDescription", e.target.value)
              }
              placeholder="Enter commercial description"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Commercial Type *</Form.Label>
                <Form.Select
                  value={formData.commercialType}
                  onChange={(e) =>
                    handleChange("commercialType", e.target.value)
                  }
                  isInvalid={!!errors.commercialType}
                >
                  <option value="">Select Commercial Type</option>
                  {commercialTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.commercialType}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>{/* Empty column for layout balance */}</Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Amount</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.commercialPlannedRevenue}
                  onChange={(e) =>
                    handleChange(
                      "commercialPlannedRevenue",
                      formatCurrency(e.target.value)
                    )
                  }
                  isInvalid={!!errors.commercialPlannedRevenue}
                  placeholder="Enter planned revenue"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.commercialPlannedRevenue}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  value={formData.commercialCurrency}
                  onChange={(e) =>
                    handleChange("commercialCurrency", e.target.value)
                  }
                  disabled={currencyLoading}
                >
                  <option value="">Select Currency</option>
                  {currencies.map((currency) => (
                    <option key={currency.value} value={currency.value}>
                      {currency.label}
                    </option>
                  ))}
                </Form.Select>
                {currencyLoading && (
                  <Form.Text className="text-muted">
                    Loading currencies...
                  </Form.Text>
                )}
                {!currencyLoading && currencies.length === 0 && (
                  <Form.Text className="text-danger">
                    No currencies available
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Project ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.commercialProjectBelong}
                  onChange={(e) =>
                    handleChange("commercialProjectBelong", e.target.value)
                  }
                  isInvalid={!!errors.commercialProjectBelong}
                  disabled={!!projectId}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.commercialProjectBelong}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organization ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.orgId}
                  onChange={(e) => handleChange("orgId", e.target.value)}
                  isInvalid={!!errors.orgId}
                  disabled={!!orgId}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.orgId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Created By</Form.Label>
            <Form.Control
              type="text"
              value={formData.createdBy}
              onChange={(e) => handleChange("createdBy", e.target.value)}
              disabled
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading
              ? "Saving..."
              : commercial
              ? "Update Commercial"
              : "Create Commercial"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CommercialModal;

// import { useState, useEffect } from "react";
// import { Modal, Button, Form, Alert, Row, Col } from "react-bootstrap";
// import { toast } from "react-toastify";
// import {
//   saveCommercialDetails,
//   updateCommercial,
// } from "../Api/CommercialMaster";
// import { fetchCurrencies } from "../Api/ViewCurrencyMaster"; // Import the currency API function

// const CommercialModal = ({
//   show,
//   onHide,
//   commercial,
//   projectId,
//   onCommercialSaved,
// }) => {
//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId;
//   const orgId = userDetails?.organisationId;

//   const [formData, setFormData] = useState({
//     commercialTitle: "",
//     commercialDescription: "",
//     commercialProjectBelong: projectId || "",
//     commercialPlannedRevenue: "",
//     commercialCurrency: "",
//     orgId: String(orgId) || "",
//     createdBy: String(userId) || "",
//   });

//   const [loading, setLoading] = useState(false);
//   const [currencies, setCurrencies] = useState([]); // State for currency options
//   const [currencyLoading, setCurrencyLoading] = useState(true); // Loading state for currencies
//   const [errors, setErrors] = useState({});

//   // Fetch currencies from API on component mount
//   useEffect(() => {
//     const loadCurrencies = async () => {
//       try {
//         setCurrencyLoading(true);
//         const currencyData = await fetchCurrencies();

//         // Transform the API response to match the expected format
//         const transformedCurrencies = currencyData.map((currency) => ({
//           value: currency.currencyCode,
//           label: `${currency.currencyCode} - ${currency.currencyName} (${currency.symbol})`,
//           originalData: currency, // Keep original data if needed
//         }));

//         setCurrencies(transformedCurrencies);
//       } catch (error) {
//         console.error("Error fetching currencies:", error);
//         toast.error("Failed to load currency data");
//         // Fallback to empty array
//         setCurrencies([]);
//       } finally {
//         setCurrencyLoading(false);
//       }
//     };

//     if (show) {
//       loadCurrencies();
//     }
//   }, [show]); // Fetch when modal is shown

//   useEffect(() => {
//     if (commercial) {
//       // Edit mode
//       setFormData({
//         commercialTitle: commercial.commercialTitle || "",
//         commercialDescription: commercial.commercialDescription || "",
//         commercialProjectBelong:
//           commercial.commercialProjectBelong || projectId || "",
//         commercialPlannedRevenue: commercial.commercialPlannedRevenue || "",
//         commercialCurrency: commercial.commercialCurrency || "",
//         orgId: commercial.orgId || String(orgId) || "",
//         createdBy: commercial.createdBy || userId?.toString() || "",
//       });
//     } else {
//       // Create mode
//       setFormData({
//         commercialTitle: "",
//         commercialDescription: "",
//         commercialProjectBelong: projectId || "",
//         commercialPlannedRevenue: "",
//         commercialCurrency: "",
//         orgId: String(orgId) || "",
//         createdBy: String(userId?.toString()) || "",
//       });
//     }
//     setErrors({});
//   }, [commercial, show, projectId, userId, orgId]);

//   const validateForm = () => {
//     const newErrors = {};

//     if (!formData.commercialTitle.trim()) {
//       newErrors.commercialTitle = "Commercial title is required";
//     }

//     if (!formData.commercialProjectBelong) {
//       newErrors.commercialProjectBelong = "Project ID is required";
//     }

//     if (!formData.orgId) {
//       newErrors.orgId = "Organization ID is required";
//     }

//     if (
//       formData.commercialPlannedRevenue &&
//       isNaN(formData.commercialPlannedRevenue)
//     ) {
//       newErrors.commercialPlannedRevenue =
//         "Planned revenue must be a valid number";
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!validateForm()) {
//       return;
//     }

//     setLoading(true);
//     try {
//       if (commercial) {
//         // Update existing commercial
//         const updateData = {
//           commercialTitle: formData.commercialTitle,
//           commercialDescription: formData.commercialDescription,
//           commercialProjectBelong: formData.commercialProjectBelong,
//           commercialPlannedRevenue: formData.commercialPlannedRevenue,
//           commercialCurrency: formData.commercialCurrency,
//           orgId: formData.orgId,
//           changedBy: userId?.toString() || "",
//         };
//         await updateCommercial(commercial.commercialId, updateData);
//         toast.success("Commercial updated successfully");
//       } else {
//         // Create new commercial
//         await saveCommercialDetails(formData);
//         toast.success("Commercial created successfully");
//       }

//       onCommercialSaved();
//       onHide();
//     } catch (error) {
//       console.error("Error saving commercial:", error);
//       toast.error(error.message || "Failed to save commercial");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleChange = (field, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));

//     // Clear error when user starts typing
//     if (errors[field]) {
//       setErrors((prev) => ({
//         ...prev,
//         [field]: "",
//       }));
//     }
//   };

//   const formatCurrency = (value) => {
//     // Remove any non-numeric characters except decimal point
//     const numericValue = value.replace(/[^\d.]/g, "");
//     return numericValue;
//   };

//   return (
//     <Modal show={show} onHide={onHide} size="lg">
//       <Modal.Header closeButton>
//         <Modal.Title>
//           {commercial ? "Edit Commercial" : "Add New Commercial"}
//         </Modal.Title>
//       </Modal.Header>

//       <Form onSubmit={handleSubmit}>
//         <Modal.Body>
//           {errors.general && <Alert variant="danger">{errors.general}</Alert>}

//           <Form.Group className="mb-3">
//             <Form.Label>Commercial Title *</Form.Label>
//             <Form.Control
//               type="text"
//               value={formData.commercialTitle}
//               onChange={(e) => handleChange("commercialTitle", e.target.value)}
//               isInvalid={!!errors.commercialTitle}
//               placeholder="Enter commercial title"
//             />
//             <Form.Control.Feedback type="invalid">
//               {errors.commercialTitle}
//             </Form.Control.Feedback>
//           </Form.Group>

//           <Form.Group className="mb-3">
//             <Form.Label>Commercial Description</Form.Label>
//             <Form.Control
//               as="textarea"
//               rows={3}
//               value={formData.commercialDescription}
//               onChange={(e) =>
//                 handleChange("commercialDescription", e.target.value)
//               }
//               placeholder="Enter commercial description"
//             />
//           </Form.Group>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Amount</Form.Label>
//                 <Form.Control
//                   type="text"
//                   value={formData.commercialPlannedRevenue}
//                   onChange={(e) =>
//                     handleChange(
//                       "commercialPlannedRevenue",
//                       formatCurrency(e.target.value)
//                     )
//                   }
//                   isInvalid={!!errors.commercialPlannedRevenue}
//                   placeholder="Enter planned revenue"
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.commercialPlannedRevenue}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Currency</Form.Label>
//                 <Form.Select
//                   value={formData.commercialCurrency}
//                   onChange={(e) =>
//                     handleChange("commercialCurrency", e.target.value)
//                   }
//                   disabled={currencyLoading}
//                 >
//                   <option value="">Select Currency</option>
//                   {currencies.map((currency) => (
//                     <option key={currency.value} value={currency.value}>
//                       {currency.label}
//                     </option>
//                   ))}
//                 </Form.Select>
//                 {currencyLoading && (
//                   <Form.Text className="text-muted">
//                     Loading currencies...
//                   </Form.Text>
//                 )}
//                 {!currencyLoading && currencies.length === 0 && (
//                   <Form.Text className="text-danger">
//                     No currencies available
//                   </Form.Text>
//                 )}
//               </Form.Group>
//             </Col>
//           </Row>

//           <Row>
//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Project ID *</Form.Label>
//                 <Form.Control
//                   type="text"
//                   value={formData.commercialProjectBelong}
//                   onChange={(e) =>
//                     handleChange("commercialProjectBelong", e.target.value)
//                   }
//                   isInvalid={!!errors.commercialProjectBelong}
//                   disabled={!!projectId}
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.commercialProjectBelong}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>

//             <Col md={6}>
//               <Form.Group className="mb-3">
//                 <Form.Label>Organization ID *</Form.Label>
//                 <Form.Control
//                   type="text"
//                   value={formData.orgId}
//                   onChange={(e) => handleChange("orgId", e.target.value)}
//                   isInvalid={!!errors.orgId}
//                   disabled={!!orgId}
//                 />
//                 <Form.Control.Feedback type="invalid">
//                   {errors.orgId}
//                 </Form.Control.Feedback>
//               </Form.Group>
//             </Col>
//           </Row>

//           <Form.Group className="mb-3">
//             <Form.Label>Created By</Form.Label>
//             <Form.Control
//               type="text"
//               value={formData.createdBy}
//               onChange={(e) => handleChange("createdBy", e.target.value)}
//               disabled
//             />
//           </Form.Group>
//         </Modal.Body>

//         <Modal.Footer>
//           <Button variant="secondary" onClick={onHide} disabled={loading}>
//             Cancel
//           </Button>
//           <Button variant="primary" type="submit" disabled={loading}>
//             {loading
//               ? "Saving..."
//               : commercial
//               ? "Update Commercial"
//               : "Create Commercial"}
//           </Button>
//         </Modal.Footer>
//       </Form>
//     </Modal>
//   );
// };

// export default CommercialModal;
